import { memo, useState } from 'react'
import { MRT_ColumnDef } from 'mantine-react-table'
import NetworkPageList from 'src/containers/Network/NetworkPageList'
import {
	Avatar,
	Badge,
	Flex,
	Image,
	Modal,
	Stack,
	Text,
} from '@mantine/core'
import {
	IconMapPin,
	IconUsers,
	IconWifi,
} from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'

const formatNetworkRole = (type?: string) => {
	if (type === 'OWNER') return 'ADMIN'
	if (type === 'ADMIN') return 'CO-ADMIN'
	return type || '-'
}

const normalizeAdmins = (value: any) => {
	return Array.isArray(value) ? value : []
}

const STATUS_META = {
	active: { label: 'Active', color: 'green' },
	less_active: { label: 'Less active', color: 'orange' },
	inactive: { label: 'Inactive', color: 'red' },
}

function NetworkPage() {
	const navigate = useNavigate()
	const [previewAvatar, setPreviewAvatar] = useState<string | null>(null)
	const columns: MRT_ColumnDef[] = [
		{
			accessorKey: 'no',
			header: 'No.',
			size: 52,
		},
		{
			accessorKey: 'title',
			header: 'Title',
			size: 280,
			Cell: ({ row }) => {
				const original = row.original as any
				const avatar = original?.avatar
				const description = original?.about || original?.bio || '-'
				return (
					<Flex align="center" gap={12} sx={{ minWidth: 250 }}>
						<Avatar
							src={avatar}
							radius={50}
							size={52}
							onClick={() => {
								if (avatar) setPreviewAvatar(avatar)
							}}
							sx={{
								flex: '0 0 auto',
								cursor: avatar ? 'zoom-in' : 'default',
							}}
						/>
						<Stack spacing={3} sx={{ minWidth: 0 }}>
							<Text size="sm" fw={700} lineClamp={1}>
								{original?.title || '-'}
							</Text>
							<Text size="xs" color="dimmed" lineClamp={2}>
								{description}
							</Text>
						</Stack>
					</Flex>
				)
			},
		},
		{
			accessorKey: '_network_admins',
			header: 'Admin',
			size: 180,
			Cell: ({ cell }) => {
				const admins = normalizeAdmins(cell.getValue())
				const owner = admins[0]
				if (!owner) return <Text size="sm">-</Text>

				return (
					<Stack spacing={4} sx={{ minWidth: 150 }}>
						<Flex
							align="center"
							gap={8}
							onClick={() => {
								if (owner?.id) navigate(`/users/${owner.id}`)
							}}
							sx={{
								cursor: owner?.id ? 'pointer' : 'default',
							}}
						>
							<Avatar src={owner.avatar} radius={50} size={28} />
							<Stack spacing={0} sx={{ minWidth: 0 }}>
								<Text size="sm" fw={700} lineClamp={1}>
									{owner.name || '-'}
								</Text>
								<Text size="xs" color="dimmed" lineClamp={1}>
									{admins.length > 1
										? `${admins.length - 1} sub admins`
										: formatNetworkRole(owner.type)}
								</Text>
							</Stack>
						</Flex>
					</Stack>
				)
			},
		},
		{
			accessorKey: 'main_category',
			header: 'Main category',
			size: 160,
			Cell: ({ cell }) => {
				const category = cell.getValue<any>()
				if (!category?.name) return <Text size="sm">-</Text>
				return (
					<Badge radius={6} color="green" variant="light">
						{category.name}
					</Badge>
				)
			},
		},
		{
			accessorKey: 'access_type',
			header: 'Type',
			size: 110,
			Cell: ({ cell }) => {
				const isPrivate = cell.getValue<string>() === 'PRIVATE'
				return (
					<Badge radius={6} color={isPrivate ? 'orange' : 'blue'} variant="light">
						{isPrivate ? 'Private' : 'Public'}
					</Badge>
				)
			},
		},
		{
			accessorKey: 'address',
			header: 'Location',
			size: 210,
			Cell: ({ row, cell }) => {
				const original = row.original as any
				const address = cell.getValue<string>()
				const isOnline = Boolean(original?.is_online)

				if (!isOnline && !address) return <Text size="sm">-</Text>

				return (
					<Stack spacing={6} sx={{ maxWidth: 190 }}>
						{isOnline && (
							<Badge
								leftSection={<IconWifi size={14} />}
								color="green"
								variant="light"
								radius={6}
							>
								Online
							</Badge>
						)}
						{address && (
							<Flex align="center" gap={6}>
								<IconMapPin size={16} color="#e26a2c" />
								<Text size="sm" lineClamp={2}>
									{address}
								</Text>
							</Flex>
						)}
					</Stack>
				)
			},
		},
		{
			accessorKey: 'amount_of_user',
			header: 'Members',
			size: 110,
			Cell: ({ cell }) => (
				<Flex align="center" gap={6}>
					<IconUsers size={16} color="#64748b" />
					<Text size="sm" fw={600}>
						{cell.getValue<string>() || '0'}
					</Text>
				</Flex>
			),
		},
		{
			accessorKey: 'activity_status',
			header: 'Status',
			size: 130,
			Cell: ({ cell }) => {
				const meta =
					STATUS_META[cell.getValue<string>()] || STATUS_META.inactive
				return (
					<Badge radius={6} variant="light" color={meta.color}>
						{meta.label}
					</Badge>
				)
			},
		},
		{
			accessorKey: 'created_at',
			header: 'Created at',
			size: 135,
		},
		{
			accessorKey: '_last_active',
			header: 'Last active',
			size: 135,
		},
	]

	return (
		<>
			<NetworkPageList columns={columns} />
			<Modal
				opened={!!previewAvatar}
				onClose={() => setPreviewAvatar(null)}
				centered
				size="lg"
				withCloseButton
			>
				{previewAvatar && (
					<Image
						src={previewAvatar}
						fit="contain"
						sx={{
							maxHeight: '75vh',
							img: {
								maxHeight: '75vh',
								objectFit: 'contain',
							},
						}}
					/>
				)}
			</Modal>
		</>
	)
}

export default memo(NetworkPage)
